import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Breadcrumbs, Link, Grid, Card, CardMedia, CardContent, Divider } from '@mui/material';
import apiClient from '../../services/api';
import { usePageReady } from '../../context/PageReadyContext';

const PostDetails = () => {
  const { signalHeroReady } = usePageReady();
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [otherPosts, setOtherPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [postResp, postsResp] = await Promise.all([
          apiClient.getPost(id),
          apiClient.getPosts(),
        ]);
        if (isMounted) {
          setPost(postResp.data);
          const others = (postsResp.data || []).filter((p) => p.id !== id).slice(0, 3);
          setOtherPosts(others);
        }
      } catch (e) {
        if (isMounted) setError(e.message || 'Failed to load post');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    load();
    return () => { isMounted = false; };
  }, [id]);

  useEffect(() => {
    if (!loading) signalHeroReady();
  }, [loading, signalHeroReady]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography>Post not found.</Typography>
      </Container>
    );
  }

  return (
    <Box>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} color="inherit" to="/">Home</Link>
          <Typography color="text.primary">{post.title}</Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            {post.image && (
              <Box sx={{ mb: 3 }}>
                <Card>
                  <CardMedia component="img" image={post.image} alt={post.title} />
                </Card>
              </Box>
            )}
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>{post.title}</Typography>
            {(post.showDate || post.showTime) && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                By {post.author}
                {post.showDate && post.date ? ` • ${new Date(post.date).toLocaleDateString()}` : ''}
                {post.showTime && post.time ? ` • ${post.time}` : ''}
              </Typography>
            )}
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
              {post.content}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            {/* Reserved for sidebar or nothing for now */}
          </Grid>
        </Grid>

        {otherPosts.length > 0 && (
          <Box sx={{ mt: 8 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>More posts</Typography>
            <Grid container spacing={3}>
              {otherPosts.map((p) => (
                <Grid item xs={12} md={4} key={p.id}>
                  <Card component={RouterLink} to={`/posts/${p.id}`} sx={{ textDecoration: 'none' }}>
                    {p.image && (
                      <CardMedia component="img" height={180} image={p.image} alt={p.title} />
                    )}
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{p.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(p.date).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PostDetails;


